import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Modal, Grid } from '@mui/material';
import { useAuth } from 'context/Auth';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import StripeCheckoutForm from 'components/StripeCheckoutForm';
import stripeService from 'services/stripe';
import { CheckIcon } from '@heroicons/react/20/solid';
import { STRIPE_PUBLIC_KEY } from 'config';
import darrenFaulknerImg from 'assets/images/darren-faulkner-3FzYpZjE5Ec-unsplash.jpg';

const stripePromise = loadStripe(STRIPE_PUBLIC_KEY!);

const Plans = () => {
  const { user, refreshUser } = useAuth();
  const [plans, setPlans] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedPriceId, setSelectedPriceId] = React.useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [clientSecret, setClientSecret] = React.useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Check for payment return status
  useEffect(() => {
    const handlePaymentRedirect = async () => {
      // Check for payment_intent or setup_intent in query params
      const searchParams = new URLSearchParams(location.search);
      const paymentIntent = searchParams.get('payment_intent');
      const setupIntent = searchParams.get('setup_intent');

      if (paymentIntent || setupIntent) {
        try {
          // Finalize the subscription with the payment
          await stripeService.createSubscription({
            paymentIntentId: paymentIntent,
            setupIntentId: setupIntent
          });

          // Refresh user to get updated subscription status
          await refreshUser();

          // Clear URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (error) {
          console.error('Error finalizing payment:', error);
        }
      }
    };

    handlePaymentRedirect();
  }, [location.search, refreshUser]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await stripeService.getPlans();
        setPlans(response);
      } catch (error) {
        console.error('Error fetching plans:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const handleSubscribe = async (priceId: string) => {
    try {
      // Create a payment intent or setup intent on the server
      const { clientSecret } = await stripeService.createSetupIntent(priceId);
      setClientSecret(clientSecret);
      setSelectedPriceId(priceId);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error creating setup intent:', error);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedPriceId(null);
    setClientSecret(null);
  };

  const handleSuccess = async () => {
    await refreshUser();
    setIsModalOpen(false);
    setSelectedPriceId(null);
    setClientSecret(null);
    navigate('/');
  };

  const formatPrice = (amount: number) => {
    return `$${(amount / 100).toFixed(2)}`;
  };

  const getIncludedFeatures = (plan: any) => {
    switch (plan.product.name) {
      case 'Community Kickoff':
        return [
          'Basic Event Management',
          'News Management',
          'Local Business Listings',
          'Essential AI Content Tools',
        ];

      case 'Small City Pro':
        return [
          'Unlimited Events & News Listings',
          'Advanced AI Content Generation',
          'Mailing List Management',
          'Community Group Pages',
          'Basic Sponsor Tools',
        ];

      case 'Big City Growth':
        return [
          'Automated News & Event Updates',
          'Advanced Ad Management',
          'Custom Integrations & Embeds',
          'API Access & Webhooks',
          'Recurring Event Scheduling',
        ];

      case 'Metro Elite':
        return [
          'Everything in Big City Growth',
          'Full Custom Branding',
          'Premium Ad Management Tools',
          '24/7 Priority Support',
          'White Label API Access',
          'City-wide Customization Options',
        ];

      default:
        return [
          'Basic Event Management',
          'News Management',
          'Local Business Listings',
        ];
    }
  };


  return (
    <Grid container height="100vh">
      <Grid
        item
        md={4}
        sx={{
          backgroundImage: `url(${darrenFaulknerImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      <Grid item xs={12} md={8} sx={{ display: "flex", alignItems: "center", justifyContent: "center", overflowY: "auto" }}>
        <Box sx={{ maxWidth: "100%", p: 4 }}>
          <Typography variant="h4" component="h1" sx={{ mb: 2, textAlign: "center" }}>
            Choose Your Plan
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, textAlign: "center" }}>
            Select a subscription plan that works for you
          </Typography>

          {loading ? (
            <Box sx={{ p: 4 }}><Typography>Loading plans...</Typography></Box>
          ) : plans.length === 0 ? (
            <Box sx={{ p: 4 }}><Typography>No plans found</Typography></Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              {plans.map((plan) => (
                <Box
                  key={plan.product.id}
                  sx={{
                    mb: 4,
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}
                >
                  <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
                    <Typography variant="h5" fontWeight="bold">
                      {plan.product.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {plan.product.description}
                    </Typography>

                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" color="primary" fontWeight="bold">
                        What's included
                      </Typography>
                      <Box component="ul" sx={{ pl: 0, mt: 1, listStyle: 'none' }}>
                        {getIncludedFeatures(plan).map((feature) => (
                          <Box
                            component="li"
                            key={feature}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              mb: 1
                            }}
                          >
                            <CheckIcon style={{ width: 20, height: 20, color: '#4f46e5', marginRight: 8 }} />
                            <Typography variant="body2">{feature}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ p: 3, bgcolor: '#f9fafb' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', mb: 2 }}>
                      <Typography variant="h4" component="span" fontWeight="bold">
                        {formatPrice(plan.prices[0].unit_amount)}
                      </Typography>
                      <Typography variant="subtitle1" component="span" sx={{ ml: 1 }}>
                        /month
                      </Typography>
                    </Box>

                    <button
                      onClick={() => handleSubscribe(plan.prices[0].id)}
                      className="w-full rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                      Subscribe
                    </button>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Grid>

      <Modal
        open={isModalOpen}
        onClose={handleModalClose}
        aria-labelledby="payment-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}>
          {selectedPriceId && clientSecret && (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: {
                  theme: 'stripe',
                },
              }}
            >
              <StripeCheckoutForm
                priceId={selectedPriceId}
                userId={user.id}
                amount={plans.find((plan) => plan.prices[0].id === selectedPriceId)?.prices[0].unit_amount}
                currency="usd"
                onSuccess={handleSuccess}
              />
            </Elements>
          )}
        </Box>
      </Modal>
    </Grid>
  );
};

export default Plans;
